export const MIXED_DEAL_PRICING = 'mixed-alarm-only' as const

export const isAlarmPricingKey = (key: string): boolean =>
  [
    'security_cams',
    'security_wifi_cameras',
    'security_home_automation',
    'security_system',
    'security_tv',
  ].includes(key)

export interface DealPriceItem {
  readonly key: string
  readonly cost: number
  readonly price: number
  readonly locked: boolean
}

export const partitionDealPricing = (items: readonly DealPriceItem[]) => {
  const alarm = items.filter(({ key }) => isAlarmPricingKey(key))
  const homeImprovement = items.filter(({ key }) => !isAlarmPricingKey(key))
  const sum = (rows: readonly DealPriceItem[], key: 'cost' | 'price') =>
    rows.reduce((total, row) => total + row[key], 0)
  return {
    alarm,
    homeImprovement,
    isMixed: alarm.length > 0 && homeImprovement.length > 0,
    alarmCost: sum(alarm, 'cost'),
    alarmPrice: sum(alarm, 'price'),
    homeImprovementCost: sum(homeImprovement, 'cost'),
    homeImprovementPrice: sum(homeImprovement, 'price'),
  }
}

/** Fixed product prices are deducted before distributing only the alarm loan. */
export const allocateMixedAlarm = (
  items: readonly DealPriceItem[],
  grandTotal: number,
  alarmLocked: boolean,
): { prices: Readonly<Record<string, number>>; valid: boolean } => {
  const alarm = items.filter(({ key }) => isAlarmPricingKey(key))
  const fixed = alarm.filter((item) => alarmLocked || item.locked)
  const variable = alarm.filter((item) => !alarmLocked && !item.locked)
  const fixedTotal = fixed.reduce((total, item) => total + item.price, 0)
  const cost = variable.reduce((total, item) => total + item.cost, 0)
  const remaining = grandTotal - fixedTotal
  const valid =
    Number.isFinite(grandTotal) &&
    grandTotal >= 0 &&
    items.every(
      (item) =>
        Number.isFinite(item.cost) &&
        Number.isFinite(item.price) &&
        item.cost >= 0 &&
        item.price >= 0,
    ) &&
    remaining >= -0.0001 &&
    (cost > 0 || Math.abs(remaining) < 0.0001)
  if (!valid) return { prices: {}, valid: false }

  const prices: Record<string, number> = {}
  for (const item of fixed) prices[item.key] = item.price
  // Allocate at the precision used by category aggregates; give float residue
  // to the last priced category, never to a locked category or an HI product.
  let left = Math.max(0, remaining)
  const priced = variable.filter((item) => item.cost > 0)
  for (const item of variable) {
    const price =
      item === priced[priced.length - 1]
        ? left
        : cost > 0
        ? Number(((Math.max(0, remaining) * item.cost) / cost).toFixed(4))
        : 0
    prices[item.key] = price
    left -= price
  }
  return { prices, valid: true }
}
